// The state machine behind the two design journeys — the membership
// application and the event booking.
//
// In Figma both are React components holding their screen and answers in
// state, with no server round trip between steps. This reproduces that: every
// screen is rendered into the page once and marked with data-screen, and this
// moves between them, keeps the answers, and drives the progress rail.
//
// Answers live in sessionStorage so a reload does not lose them, and nothing
// leaves the browser — these journeys are on mock data until their steps are
// wired to Dynamics and Commerce.

interface JourneyStep {
  label: string;
  screens: string[];
}

interface JourneyOptions {
  /** The element wrapping every [data-screen]. */
  root: HTMLElement;
  /** Steps in order; a screen belongs to the step that lists it. */
  steps: JourneyStep[];
  /** Screen to open on first load. */
  initialScreen: string;
  /** Key under which answers are kept for this journey. */
  storageKey: string;
  /**
   * Screens that move on by themselves after a beat — the design's
   * "analysing" and "processing" screens — mapped to where they go.
   */
  autoAdvance?: Record<string, { to: string; afterMs: number }>;
  /** Runs after every screen change, for anything a screen needs to recompute. */
  onScreen?: (screen: string, answers: Record<string, unknown>) => void;
}

export function initDesignJourney(options: JourneyOptions) {
  const { root, steps, initialScreen, storageKey, autoAdvance = {} } = options;

  // The journeys need to recompute derived values — the recommended grade, the
  // amount due — after every answer. A function cannot travel through the
  // config attribute, so each journey view publishes one on window and it is
  // picked up here.
  const onScreen =
    options.onScreen ??
    ((screen: string, answers: Record<string, unknown>) =>
      (window as unknown as { imJourneyPaint?: (s: string, a: Record<string, unknown>) => void })
        .imJourneyPaint?.(screen, answers));

  const screens = new Map<string, HTMLElement>();
  root.querySelectorAll<HTMLElement>('[data-screen]').forEach(el => {
    screens.set(el.dataset.screen!, el);
  });

  const answers: Record<string, unknown> = readAnswers(storageKey);
  const history: string[] = [];
  let current = '';
  let autoAdvanceTimer: number | undefined;

  function readAnswers(key: string): Record<string, unknown> {
    try {
      return JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch {
      // A private window or blocked storage: the journey still works, it just
      // will not survive a reload.
      return {};
    }
  }

  function saveAnswers() {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(answers));
    } catch {
      /* not fatal — see readAnswers */
    }
  }

  function stepIndexOf(screen: string) {
    return steps.findIndex(s => s.screens.includes(screen));
  }

  /** Paints the sidebar rail, the mobile bar and the progress line. */
  function paintProgress(screen: string) {
    const active = stepIndexOf(screen);
    if (active < 0) return;

    const pct = Math.round(((active + 1) / steps.length) * 100);

    root.querySelectorAll<HTMLElement>('[data-progress-fill]').forEach(el => {
      el.style.width = pct + '%';
    });
    root.querySelectorAll<HTMLElement>('[data-step-count]').forEach(el => {
      el.textContent = `Step ${active + 1} of ${steps.length}`;
    });
    root.querySelectorAll<HTMLElement>('[data-step-label]').forEach(el => {
      el.textContent = steps[active].label;
    });

    root.querySelectorAll<HTMLElement>('[data-step-index]').forEach(el => {
      const i = Number(el.dataset.stepIndex);
      const done = i < active;
      const isActive = i === active;

      el.classList.toggle('step-done', done);
      el.classList.toggle('step-active', isActive);

      // The booking journey's rail uses the same states on its own classes.
      el.querySelectorAll('.booking-step-num, .progress-step-num').forEach(n => {
        n.classList.toggle('done', done);
        n.classList.toggle('active', isActive);
      });
      el.querySelectorAll('.booking-step-label, .progress-step-label').forEach(n => {
        n.classList.toggle('done', done);
        n.classList.toggle('active', isActive);
      });

      // A tick replaces the number once a step is behind you.
      const tick = el.querySelector<HTMLElement>('[data-step-tick]');
      const num = el.querySelector<HTMLElement>('[data-step-number]');
      if (tick) tick.hidden = !done;
      if (num) num.hidden = done;
    });
  }

  function show(screen: string, { record = true } = {}) {
    if (!screens.has(screen)) {
      console.warn(`[design-journey] no screen "${screen}"`);
      return;
    }

    if (autoAdvanceTimer) {
      window.clearTimeout(autoAdvanceTimer);
      autoAdvanceTimer = undefined;
    }

    if (record && current && current !== screen) history.push(current);
    current = screen;

    screens.forEach((el, name) => { el.hidden = name !== screen; });
    paintProgress(screen);

    // Each screen starts at the top of its own content, not wherever the last
    // one was scrolled to.
    const main = root.querySelector('.journey-main');
    if (main) main.scrollTop = 0;
    window.scrollTo({ top: 0 });

    onScreen?.(screen, answers);
    paintAnswerStates();

    const auto = autoAdvance[screen];
    if (auto) {
      autoAdvanceTimer = window.setTimeout(() => show(auto.to), auto.afterMs);
    }
  }

  function back() {
    const previous = history.pop();
    if (previous) show(previous, { record: false });
  }

  // ── Answer capture ─────────────────────────────────────────────────────────
  //
  // Three shapes, matching the design's controls:
  //   [data-answer]              a single value — choice cards, radios, inputs
  //   [data-answer-multi]        a set — the interest and community chips
  //   [data-answer-toggle]       a flag — the comms toggles

  function paintAnswerStates() {
    root.querySelectorAll<HTMLElement>('[data-answer]').forEach(el => {
      const key = el.dataset.answer!;
      const value = el.dataset.answerValue;

      if (value === undefined) {
        // An input rather than a button.
        const input = el as HTMLInputElement;
        const stored = answers[key];
        if (typeof stored === 'string' && input.value !== stored) input.value = stored;
        return;
      }

      const selected = answers[key] === value;
      el.classList.toggle('selected', selected);
      el.setAttribute('aria-pressed', String(selected));
    });

    root.querySelectorAll<HTMLElement>('[data-answer-multi]').forEach(el => {
      const key = el.dataset.answerMulti!;
      const value = el.dataset.answerValue!;
      const set = (answers[key] as string[]) || [];
      const selected = set.includes(value);
      el.classList.toggle('selected', selected);
      el.setAttribute('aria-pressed', String(selected));
    });

    root.querySelectorAll<HTMLInputElement>('[data-answer-toggle]').forEach(el => {
      const key = el.dataset.answerToggle!;
      if (key in answers) el.checked = Boolean(answers[key]);
    });

    // Anything showing an answer back to you — the review screen, the cost
    // panel — is refreshed from the same store.
    root.querySelectorAll<HTMLElement>('[data-answer-echo]').forEach(el => {
      const key = el.dataset.answerEcho!;
      const value = answers[key];
      el.textContent = Array.isArray(value)
        ? value.join(', ')
        : value === undefined || value === '' ? '—' : String(value);
    });

    // Continue buttons that need an answer before they will move on.
    root.querySelectorAll<HTMLButtonElement>('[data-requires]').forEach(el => {
      const keys = el.dataset.requires!.split(',').map(k => k.trim()).filter(Boolean);
      const ready = keys.every(k => {
        const v = answers[k];
        return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== '';
      });
      el.disabled = !ready;
      el.style.opacity = ready ? '1' : '0.5';
    });
  }

  root.addEventListener('click', event => {
    const target = event.target as HTMLElement;

    const single = target.closest<HTMLElement>('[data-answer][data-answer-value]');
    if (single) {
      answers[single.dataset.answer!] = single.dataset.answerValue!;
      saveAnswers();
      onScreen?.(current, answers);
      paintAnswerStates();

      // Choice cards and radios in the design move on as soon as you pick.
      const next = single.dataset.answerNext;
      if (next) show(next);
      return;
    }

    const multi = target.closest<HTMLElement>('[data-answer-multi]');
    if (multi) {
      const key = multi.dataset.answerMulti!;
      const value = multi.dataset.answerValue!;
      const set = new Set((answers[key] as string[]) || []);
      set.has(value) ? set.delete(value) : set.add(value);
      answers[key] = [...set];
      saveAnswers();
      onScreen?.(current, answers);
      paintAnswerStates();
      return;
    }

    const goto = target.closest<HTMLElement>('[data-goto]');
    if (goto) {
      event.preventDefault();
      show(goto.dataset.goto!);
      return;
    }

    if (target.closest('[data-journey-back]')) {
      event.preventDefault();
      back();
      return;
    }

    const openSelector = target.closest<HTMLElement>('[data-open]')?.dataset.open;
    if (openSelector) {
      event.preventDefault();
      const overlay = root.querySelector<HTMLElement>(openSelector);
      if (overlay) overlay.hidden = false;
      return;
    }

    const closeSelector = target.closest<HTMLElement>('[data-close]')?.dataset.close;
    if (closeSelector) {
      event.preventDefault();
      const overlay = root.querySelector<HTMLElement>(closeSelector);
      if (overlay) overlay.hidden = true;
      return;
    }
  });

  root.addEventListener('input', event => {
    const input = event.target as HTMLInputElement;
    if (input.dataset.answer && input.dataset.answerValue === undefined) {
      answers[input.dataset.answer] = input.value;
      saveAnswers();
      onScreen?.(current, answers);
      paintAnswerStates();
    }
  });

  root.addEventListener('change', event => {
    const input = event.target as HTMLInputElement;
    if (input.dataset.answerToggle) {
      answers[input.dataset.answerToggle] = input.checked;
      saveAnswers();
      onScreen?.(current, answers);
    }
  });

  // Escape closes whichever overlay is open, as the design's drawers do.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    root.querySelectorAll<HTMLElement>('[data-overlay]').forEach(el => { el.hidden = true; });
  });

  paintAnswerStates();
  show(initialScreen, { record: false });

  return {
    show,
    back,
    answers,
    refresh: () => { paintAnswerStates(); onScreen?.(current, answers); },
  };
}
